import { useState } from "react";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { trpcClient } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Building2,
  Phone,
  Mail,
  MapPin,
  FileText,
  Globe,
  Save,
  Pencil,
  X,
  Upload,
  Clock,
  Users,
  ShieldCheck,
  CheckCircle2,
  Printer,
  Sparkles,
  Layers,
  Award,
  CreditCard,
} from "lucide-react";
import { toast } from "sonner";
import { uploadFile } from "@/lib/uploadFile";
import { validateUploadFile, MAX_AVATAR_LOGO_SIZE, extractErrorMessage } from "@/lib/utils";
import { InfoCell } from "@/components/shared/InfoCell";
import { CorporateBankCard } from "@/components/shared/CorporateBankCard";
import { DigitalLetterheadPreview } from "@/components/shared/DigitalLetterheadPreview";

export interface CompanyInfoData {
  company_name?: string;
  company_name_en?: string;
  company_short_name?: string;
  company_address_1?: string;
  company_address_2?: string;
  company_tax_code?: string;
  company_email?: string;
  company_phone?: string;
  company_fax?: string;
  company_website?: string;
  company_logo?: string;
  company_bank_name?: string;
  company_bank_account?: string;
  company_bank_branch?: string;
  company_representative?: string;
  company_representative_title?: string;
  company_stamp_img?: string;
}

const DEFAULT_COMPANY_DATA: CompanyInfoData = {
  company_name: "CÔNG TY TNHH ARTPRESTO VIỆT NAM",
  company_name_en: "ARTPRESTO VIETNAM CO., LTD",
  company_short_name: "APV",
  company_tax_code: "0700820294",
  company_representative: "Nguyễn Văn Hùng",
  company_representative_title: "Tổng Giám Đốc",
  company_phone: "0226 246 0988",
  company_fax: "0226 246 0989",
  company_email: "info@artpresto.vn",
  company_website: "https://artpresto.vn",
  company_address_1: "Lô CN09, Đường số 1, KCN Hải Sơn, Xã Đức Hòa Đông, Huyện Đức Hòa, Tỉnh Long An",
  company_address_2: "Lô C2-1, KCN Châu Sơn, Phường Châu Sơn, Thành phố Phủ Lý, Tỉnh Hà Nam",
  company_bank_name: "MUFG Bank, Ltd. - Chi nhánh Hà Nội",
  company_bank_account: "040436",
  company_bank_branch: "Chi nhánh Hà Nội",
  company_logo: "/artpresto-logo.png",
};

const ALL_CONFIG_KEYS: (keyof CompanyInfoData)[] = [
  "company_name",
  "company_name_en",
  "company_short_name",
  "company_address_1",
  "company_address_2",
  "company_tax_code",
  "company_email",
  "company_phone",
  "company_fax",
  "company_website",
  "company_logo",
  "company_bank_name",
  "company_bank_account",
  "company_bank_branch",
  "company_representative",
  "company_representative_title",
  "company_stamp_img",
];

export default function CompanyInfoPage() {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<CompanyInfoData>({});
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isUploadingStamp, setIsUploadingStamp] = useState(false);

  // Fetch system configs
  const { data: configData, isLoading } = useQuery({
    queryKey: ["system-config-all"],
    queryFn: async () => {
      const all = await trpcClient.config.getAll.query();
      return all as Record<string, any>;
    },
  });

  // Merge loaded configs with defaults to guarantee rich ground-truth data
  const companyData: CompanyInfoData = { ...DEFAULT_COMPANY_DATA };
  if (configData) {
    for (const key of ALL_CONFIG_KEYS) {
      if (configData[key] !== undefined && configData[key] !== null && configData[key] !== "") {
        companyData[key] = configData[key];
      }
    }
  }

  const startEdit = () => {
    setFormData({ ...companyData });
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setFormData({});
  };

  const handleFieldChange = (key: keyof CompanyInfoData, val: string) => {
    setFormData((prev) => ({ ...prev, [key]: val }));
  };

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (data: CompanyInfoData) => {
      const promises = ALL_CONFIG_KEYS.map((key) => {
        const value = data[key] ?? companyData[key] ?? "";
        return trpcClient.config.update.mutate({ key, value });
      });
      await Promise.all(promises);
    },
    onSuccess: async () => {
      setIsEditing(false);
      await queryClient.invalidateQueries({ queryKey: ["system-config-all"] });
      await queryClient.invalidateQueries({ queryKey: ["config-all"] });
      toast.success("Đã cập nhật thông tin doanh nghiệp thành công!");
    },
    onError: () => toast.error("Không thể lưu thông tin doanh nghiệp"),
  });

  const handleSave = () => saveMutation.mutate(formData);

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validation = validateUploadFile(file, MAX_AVATAR_LOGO_SIZE, "Logo công ty");
    if (!validation.valid) {
      toast.error(validation.error);
      e.target.value = "";
      return;
    }
    try {
      setIsUploadingLogo(true);
      const res = await uploadFile(file, { preservePng: true });
      if (isEditing) {
        setFormData((prev) => ({ ...prev, company_logo: res.url }));
      } else {
        await trpcClient.config.update.mutate({ key: "company_logo", value: res.url });
        await queryClient.invalidateQueries({ queryKey: ["system-config-all"] });
      }
      toast.success("Đã tải logo công ty thành công!");
    } catch (err: any) {
      toast.error(`Lỗi tải logo: ${extractErrorMessage(err)}`);
    } finally {
      setIsUploadingLogo(false);
      e.target.value = "";
    }
  };

  const handleStampUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validation = validateUploadFile(file, MAX_AVATAR_LOGO_SIZE, "Mẫu con dấu");
    if (!validation.valid) {
      toast.error(validation.error);
      e.target.value = "";
      return;
    }
    try {
      setIsUploadingStamp(true);
      const res = await uploadFile(file, { preservePng: true });
      if (isEditing) {
        setFormData((prev) => ({ ...prev, company_stamp_img: res.url }));
      } else {
        await trpcClient.config.update.mutate({ key: "company_stamp_img", value: res.url });
        await queryClient.invalidateQueries({ queryKey: ["system-config-all"] });
      }
      toast.success("Đã tải mẫu con dấu điện tử thành công!");
    } catch (err: any) {
      toast.error(`Lỗi tải con dấu: ${extractErrorMessage(err)}`);
    } finally {
      setIsUploadingStamp(false);
      e.target.value = "";
    }
  };

  const currentData = isEditing ? formData : companyData;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-64px)]">
        <div className="text-center">
          <Clock className="w-8 h-8 mx-auto mb-2 text-indigo-500 animate-spin" />
          <p className="text-sm text-slate-500 font-medium">Đang tải hồ sơ doanh nghiệp...</p>
        </div>
      </div>
    );
  }

    const actions = (
    <div className="flex items-center gap-2">
      {isEditing ? (
        <>
          <Button
            variant="outline"
            size="sm"
            onClick={cancelEdit}
            className="h-9 text-xs font-semibold"
          >
            <X className="w-3.5 h-3.5 mr-1" /> Hủy
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saveMutation.isPending}
            className="h-9 text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm"
          >
            <Save className="w-3.5 h-3.5 mr-1" />{" "}
            {saveMutation.isPending ? "Đang lưu..." : "Lưu thay đổi"}
          </Button>
        </>
      ) : (
        <Button
          size="sm"
          onClick={startEdit}
          className="h-9 text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm"
        >
          <Pencil className="w-3.5 h-3.5 mr-1" /> Chỉnh sửa
        </Button>
      )}
    </div>
  );

  return (
    <StandardDetailLayout
      title="Hồ Sơ Doanh Nghiệp"
      status={
        <Badge variant="outline" className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border-emerald-200">
          ● Chuẩn Enterprise
        </Badge>
      }
      breadcrumbs={[
        { label: "Hệ thống", to: "/" },
        { label: "Hồ sơ doanh nghiệp" },
      ]}
      actions={actions}
    >
      <div className="w-full mx-auto py-2 space-y-6 max-w-[1520px]">{/* ── Balanced 2-Column Responsive Layout (6/12 - 6/12) ── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* ── CỘT 1 (TRÁI): NHẬN DIỆN THƯƠNG HIỆU & PHÁP LÝ (6/12) ── */}
        <div className="lg:col-span-6 space-y-6">
          {/* Card 1: Brand Identity & Con Dấu */}
          <Card className="border-slate-200 shadow-sm overflow-hidden">
            <CardHeader className="py-3 px-4 bg-slate-50/70 border-b border-slate-100">
              <CardTitle className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center justify-between">
                <span className="flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-indigo-600" /> Nhận Diện Thương Hiệu & Con Dấu Pháp Nhân
                </span>
                <span className="text-[10px] font-mono text-emerald-600 font-bold bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                  ISO 9001:2015
                </span>
              </CardTitle>
            </CardHeader>

            <CardContent className="p-4 space-y-4">
              {/* Grid 2 ô song song: Logo & Con Dấu */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Ô Logo */}
                <div className="relative group rounded-xl border border-dashed border-slate-200 bg-slate-50/50 p-4 flex flex-col items-center justify-center min-h-[130px] text-center">
                  <div className="w-28 h-20 flex items-center justify-center">
                    {currentData.company_logo ? (
                      <img
                        src={currentData.company_logo}
                        alt="Company Logo"
                        className="max-h-full max-w-full object-contain filter drop-shadow-xs"
                        onError={(e) => (e.currentTarget.style.display = "none")}
                      />
                    ) : (
                      <div className="text-lg font-black text-indigo-700 tracking-tighter">
                        ARTPRESTO
                      </div>
                    )}
                  </div>
                  <label
                    title="Tải logo mới"
                    className="mt-2 text-[10px] text-indigo-600 hover:text-indigo-800 font-semibold cursor-pointer flex items-center gap-1"
                  >
                    <Upload className="w-3 h-3" />
                    {currentData.company_logo ? "Đổi logo" : "Tải logo"}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={isUploadingLogo}
                      onChange={handleLogoUpload}
                    />
                  </label>
                  <span className="text-[10px] text-slate-400 font-medium">Logo thương hiệu</span>
                </div>

                {/* Ô Con Dấu Tròn Điện Tử */}
                <div className="relative group rounded-xl border border-dashed border-red-200 bg-red-50/30 p-4 flex flex-col items-center justify-center min-h-[130px] text-center">
                  <div className="w-28 h-20 flex items-center justify-center">
                    {currentData.company_stamp_img ? (
                      <img
                        src={currentData.company_stamp_img}
                        alt="Con dấu điện tử"
                        className="max-h-full max-w-full object-contain filter drop-shadow-sm"
                      />
                    ) : (
                      <div className="text-center space-y-1">
                        <div className="w-12 h-12 mx-auto rounded-full border-2 border-red-500/60 border-dashed flex items-center justify-center text-red-500 font-bold text-[10px]">
                          DẤU ĐỎ
                        </div>
                        <p className="text-[10px] text-slate-400">Mẫu dấu tròn pháp nhân</p>
                      </div>
                    )}
                  </div>
                  <label
                    title="Tải mẫu con dấu"
                    className="mt-2 text-[10px] text-red-600 hover:text-red-800 font-semibold cursor-pointer flex items-center gap-1"
                  >
                    <Upload className="w-3 h-3" />
                    {currentData.company_stamp_img ? "Đổi con dấu" : "Tải ảnh con dấu (PNG)"}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={isUploadingStamp}
                      onChange={handleStampUpload}
                    />
                  </label>
                  <span className="text-[10px] text-red-600 font-medium">Con dấu tròn pháp nhân</span>
                </div>
              </div>

              {/* Status & ID pill list */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-3 border-t border-slate-100 text-xs">
                <div className="p-2 rounded-lg bg-slate-50 border border-slate-100">
                  <span className="text-slate-400 text-[10px] uppercase font-bold block mb-0.5">Mã đơn vị</span>
                  <span className="font-mono font-bold text-slate-800 text-xs">
                    {currentData.company_short_name || "APV"}
                  </span>
                </div>
                <div className="p-2 rounded-lg bg-slate-50 border border-slate-100">
                  <span className="text-slate-400 text-[10px] uppercase font-bold block mb-0.5">Mã số thuế</span>
                  <span className="font-mono font-bold text-indigo-700 text-xs">
                    {currentData.company_tax_code || "0700820294"}
                  </span>
                </div>
                <div className="col-span-2 sm:col-span-1 p-2 rounded-lg bg-slate-50 border border-slate-100">
                  <span className="text-slate-400 text-[10px] uppercase font-bold block mb-0.5">Trạng thái</span>
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Đang hoạt động
                  </span>
                </div>
              </div>

              {/* Digital Certificate Badges */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[10px]">
                <div className="flex items-center gap-2 p-2 rounded-lg bg-emerald-50/80 border border-emerald-100 text-emerald-800 font-medium">
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                  <span>Chứng nhận quản lý ISO 9001:2015</span>
                </div>
                <div className="flex items-center gap-2 p-2 rounded-lg bg-sky-50/80 border border-sky-100 text-sky-800 font-medium">
                  <CheckCircle2 className="w-3.5 h-3.5 text-sky-600 shrink-0" />
                  <span>Chữ ký số VNPT-CA / Viettel-CA</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Card 2: Legal Grid */}
          <Card className="border-slate-200 shadow-sm overflow-hidden">
            <CardHeader className="py-3 px-4 bg-slate-50/70 border-b border-slate-100 flex flex-row items-center justify-between">
              <CardTitle className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2">
                <FileText className="w-4 h-4 text-indigo-600" />
                Thông Tin Pháp Lý Doanh Nghiệp (ĐKKD)
              </CardTitle>
            </CardHeader>

            <CardContent className="p-4 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
                <InfoCell
                  label="Tên đầy đủ công ty (Tiếng Việt)"
                  value={currentData.company_name}
                  colSpan={4}
                  icon={<Building2 className="w-3.5 h-3.5" />}
                  isEditing={isEditing}
                  editValue={formData.company_name || ""}
                  onChange={(val) => handleFieldChange("company_name", val)}
                  valueClassName="text-sm sm:text-base font-extrabold text-indigo-950 uppercase"
                />

                <InfoCell
                  label="Tên doanh nghiệp (English)"
                  value={currentData.company_name_en}
                  colSpan={2}
                  icon={<Globe className="w-3.5 h-3.5" />}
                  isEditing={isEditing}
                  editValue={formData.company_name_en || ""}
                  onChange={(val) => handleFieldChange("company_name_en", val)}
                  valueClassName="font-semibold text-slate-700 uppercase"
                />

                <InfoCell
                  label="Tên viết tắt"
                  value={currentData.company_short_name}
                  colSpan={1}
                  icon={<Layers className="w-3.5 h-3.5" />}
                  isEditing={isEditing}
                  editValue={formData.company_short_name || ""}
                  onChange={(val) => handleFieldChange("company_short_name", val)}
                  valueClassName="font-mono font-bold text-slate-800"
                />

                <InfoCell
                  label="Mã số thuế"
                  value={currentData.company_tax_code}
                  colSpan={1}
                  icon={<FileText className="w-3.5 h-3.5" />}
                  copyable={true}
                  copyTooltip="Sao chép MST"
                  isEditing={isEditing}
                  editValue={formData.company_tax_code || ""}
                  onChange={(val) => handleFieldChange("company_tax_code", val)}
                  valueClassName="font-mono font-bold text-indigo-700 text-sm"
                />

                <InfoCell
                  label="Địa chỉ trụ sở chính (Theo ĐKKD)"
                  value={currentData.company_address_1}
                  colSpan={4}
                  icon={<MapPin className="w-3.5 h-3.5 text-rose-500" />}
                  isEditing={isEditing}
                  type="textarea"
                  editValue={formData.company_address_1 || ""}
                  onChange={(val) => handleFieldChange("company_address_1", val)}
                  valueClassName="text-xs sm:text-sm font-medium text-slate-800"
                />

                <InfoCell
                  label="Địa chỉ nhà máy & Xưởng in offset"
                  value={currentData.company_address_2}
                  colSpan={4}
                  icon={<Building2 className="w-3.5 h-3.5 text-amber-500" />}
                  isEditing={isEditing}
                  type="textarea"
                  editValue={formData.company_address_2 || ""}
                  onChange={(val) => handleFieldChange("company_address_2", val)}
                  valueClassName="text-xs sm:text-sm font-medium text-slate-800"
                />

                <InfoCell
                  label="Người đại diện theo pháp luật"
                  value={currentData.company_representative}
                  colSpan={2}
                  icon={<Users className="w-3.5 h-3.5 text-indigo-500" />}
                  isEditing={isEditing}
                  editValue={formData.company_representative || ""}
                  onChange={(val) => handleFieldChange("company_representative", val)}
                  valueClassName="font-bold text-slate-800"
                />

                <InfoCell
                  label="Chức danh người đại diện"
                  value={currentData.company_representative_title}
                  colSpan={2}
                  icon={<Users className="w-3.5 h-3.5 text-slate-500" />}
                  isEditing={isEditing}
                  editValue={formData.company_representative_title || ""}
                  onChange={(val) => handleFieldChange("company_representative_title", val)}
                  valueClassName="font-semibold text-slate-700"
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ── CỘT 2 (PHẢI): NGÂN HÀNG, TIÊU ĐỀ IN & KÊNH LIÊN LẠC (6/12) ── */}
        <div className="lg:col-span-6 space-y-6">
          {/* Card 1: Corporate Bank Card */}
          <div className="space-y-3">
            {isEditing && (
              <Card className="border-indigo-200 bg-indigo-50/30 shadow-xs">
                <CardHeader className="py-2.5 px-4 bg-indigo-50/60 border-b border-indigo-100">
                  <CardTitle className="text-xs font-bold uppercase tracking-wider text-indigo-900 flex items-center gap-2">
                    <CreditCard className="w-3.5 h-3.5 text-indigo-600" />
                    Chỉnh sửa tài khoản ngân hàng
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <InfoCell
                    label="Số tài khoản"
                    value={currentData.company_bank_account}
                    colSpan={1}
                    isEditing={isEditing}
                    editValue={formData.company_bank_account || ""}
                    onChange={(val) => handleFieldChange("company_bank_account", val)}
                  />
                  <InfoCell
                    label="Tên ngân hàng"
                    value={currentData.company_bank_name}
                    colSpan={1}
                    isEditing={isEditing}
                    editValue={formData.company_bank_name || ""}
                    onChange={(val) => handleFieldChange("company_bank_name", val)}
                  />
                  <InfoCell
                    label="Chi nhánh"
                    value={currentData.company_bank_branch}
                    colSpan={1}
                    isEditing={isEditing}
                    editValue={formData.company_bank_branch || ""}
                    onChange={(val) => handleFieldChange("company_bank_branch", val)}
                  />
                </CardContent>
              </Card>
            )}

            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-xs text-slate-500 px-1">
                <span className="font-semibold uppercase tracking-wider text-[11px] text-slate-400 flex items-center gap-1.5">
                  <Building2 className="w-3.5 h-3.5 text-amber-500" />
                  Tài Khoản Doanh Nghiệp VIP
                </span>
                <span className="text-[10px] font-mono text-indigo-600 font-bold">EMV CHIP</span>
              </div>

              <CorporateBankCard
                bankName={currentData.company_bank_name}
                accountNumber={currentData.company_bank_account}
                accountName={currentData.company_name}
                branch={currentData.company_bank_branch}
              />
            </div>
          </div>

          {/* Card 2: Letterhead Preview A4 */}
          <DigitalLetterheadPreview
            companyName={currentData.company_name}
            companyNameEn={currentData.company_name_en}
            address={currentData.company_address_2 || currentData.company_address_1}
            taxCode={currentData.company_tax_code}
            phone={currentData.company_phone}
            email={currentData.company_email}
            website={currentData.company_website}
            bankInfo={`${currentData.company_bank_name} - STK: ${currentData.company_bank_account}`}
            logoUrl={currentData.company_logo}
          />

          {/* Card 3: Contact Channels */}
          <Card className="border-slate-200 shadow-sm overflow-hidden">
            <CardHeader className="py-2.5 px-4 bg-slate-50/70 border-b border-slate-100 flex items-center justify-between">
              <CardTitle className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2">
                <Phone className="w-3.5 h-3.5 text-emerald-600" />
                Kênh Liên Lạc & Tổng Đài Vận Hành
              </CardTitle>
              <span className="text-[10px] text-slate-400">4 Kênh chính thức</span>
            </CardHeader>

            <CardContent className="p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {/* Hotline */}
                <InfoCell
                  label="Hotline / Điện thoại"
                  value={currentData.company_phone}
                  colSpan={1}
                  icon={<Phone className="w-3.5 h-3.5 text-emerald-500" />}
                  copyable={true}
                  href={currentData.company_phone ? `tel:${currentData.company_phone}` : undefined}
                  isEditing={isEditing}
                  editValue={formData.company_phone || ""}
                  onChange={(val) => handleFieldChange("company_phone", val)}
                  valueClassName="font-mono font-bold text-emerald-700"
                />

                {/* Email */}
                <InfoCell
                  label="Hộp thư tiếp nhận (Email)"
                  value={currentData.company_email}
                  colSpan={1}
                  icon={<Mail className="w-3.5 h-3.5 text-sky-500" />}
                  copyable={true}
                  href={currentData.company_email ? `mailto:${currentData.company_email}` : undefined}
                  isEditing={isEditing}
                  editValue={formData.company_email || ""}
                  onChange={(val) => handleFieldChange("company_email", val)}
                  valueClassName="font-mono text-xs font-semibold text-sky-700 break-words"
                />

                {/* Website */}
                <InfoCell
                  label="Cổng thông tin trực tuyến (Website)"
                  value={currentData.company_website}
                  colSpan={1}
                  icon={<Globe className="w-3.5 h-3.5 text-indigo-500" />}
                  href={currentData.company_website}
                  isEditing={isEditing}
                  editValue={formData.company_website || ""}
                  onChange={(val) => handleFieldChange("company_website", val)}
                  valueClassName="text-xs font-semibold text-indigo-700 break-words"
                />

                {/* Fax */}
                <InfoCell
                  label="Số Fax văn phòng"
                  value={currentData.company_fax}
                  colSpan={1}
                  icon={<Printer className="w-3.5 h-3.5 text-slate-500" />}
                  copyable={true}
                  isEditing={isEditing}
                  editValue={formData.company_fax || ""}
                  onChange={(val) => handleFieldChange("company_fax", val)}
                  valueClassName="font-mono text-xs font-semibold text-slate-700"
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      </div>
    </StandardDetailLayout>
  );
}
